import Link from '@components/Router/Link'
import { AccountContext } from '@lib/context/AccountContext/AccountContext'
import { Language } from '@lib/graphql/havelock/__generated__/graphql'
import useUser from '@lib/hooks/useUser'
import { useTranslations } from '@mono/packages/translations'
import TranslationsProvider from '@mono/packages/translations/TranslationsProvider/TranslationsProvider'
import UIComponentsProvider from '@mono/packages/ui/components/Provider/UiComponentsProvider'
import { formatDate } from '@utils/date'
import {
  getLoadedLocale,
  getLoadedTranslations,
  loadLocale,
  LocaleBundle
} from '@utils/i18n'
import { setDefaultOptions } from 'date-fns'
import { bg, enUS, fr } from 'date-fns/locale'
import Image from 'next/image'
import { ReactNode, useContext, useEffect, useState } from 'react'

const dateFnsLocales = {
  [Language.En]: enUS,
  [Language.Bg]: bg,
  [Language.Fr]: fr
}

export const UiComponentsTranslationsProvider = ({ children }) => {
  const { t } = useTranslations()

  return (
    <UIComponentsProvider
      t={t}
      formatDate={formatDate}
      ImageComponent={Image}
      LinkComponent={Link}
    >
      {children}
    </UIComponentsProvider>
  )
}

const useLocaleBundle = (requestedLang?: Language) => {
  const [loadedLocale, setLoadedLocale] = useState<
    (LocaleBundle & { lang: Language }) | null
  >(null)
  const [error, setError] = useState<unknown>(null)
  const cachedLocale = requestedLang
    ? getLoadedLocale(requestedLang)
    : undefined

  useEffect(() => {
    if (!requestedLang || cachedLocale || requestedLang === loadedLocale?.lang)
      return

    let cancelled = false

    loadLocale(requestedLang).then(
      (locale) => {
        if (!cancelled) {
          setLoadedLocale({ lang: requestedLang, ...locale })
          setError(null)
        }
      },
      (loadError) => {
        if (!cancelled) setError(loadError)
      }
    )

    return () => {
      cancelled = true
    }
  }, [requestedLang, cachedLocale, loadedLocale?.lang])

  if (error) throw error

  return cachedLocale && requestedLang
    ? { lang: requestedLang, ...cachedLocale }
    : loadedLocale
}

export const TranslationsProviderWrapper = ({
  children,
  fallbackLang
}: {
  children: ReactNode
  fallbackLang?: Language
}) => {
  const account = useContext(AccountContext)
  const requestedLang = account?.lang ?? fallbackLang
  const locale = useLocaleBundle(requestedLang)

  if (!requestedLang || locale?.lang !== requestedLang) return null

  return (
    <TranslationsProvider
      lang={locale.lang}
      translations={locale.translations}
      formLocale={locale.formLocale}
      gridLocale={locale.gridLocale}
      getTranslations={getLoadedTranslations}
    >
      {children}
    </TranslationsProvider>
  )
}

export const DateOptionsProvider = ({ children }) => {
  const { account } = useUser()
  const lang = account.lang

  useEffect(() => {
    setDefaultOptions({
      weekStartsOn: 1,
      locale: dateFnsLocales[lang]
    })
  }, [lang])

  return children
}
